import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { apiClient } from '../lib/api'
import { Button } from './ui/Button'
import { Dialog } from './ui/Dialog'
import { Input, Select } from './ui/Input'

type AgileTask = {
  id: string
  title: string
  status: string
  estimatedHours?: string | null
}

type AgileUserStory = { id: string; title: string; tasks: AgileTask[] }

/**
 * Shape returned by GET /agile/tree: features are flat objects at the top
 * level (no wrapper row), with nested userStories[] → tasks[]. Project
 * name is NOT inlined — fetched separately and joined client-side via
 * projectId / clientId.
 */
type AgileFeature = {
  id: string
  title: string
  projectId?: string | null
  clientId?: string | null
  userStories: AgileUserStory[]
}
type AgileTree = { features: AgileFeature[] }

type ProjectMini = { id: string; name: string }
type ProjectsResult = { results: ProjectMini[] }

type FlatTask = {
  id: string
  label: string
  estimatedHours: number | null
}

const DURATION_PRESETS = [5, 10, 15, 30, 45, 60, 90, 120]

/**
 * Quick-log a CLOSED time entry for past work (calls, IDE, hand-written
 * emails). Closes the gap toward 100% tracking coverage (Tracking STRICT).
 *
 * UI:
 * - Task picker (flattens active tasks from /agile/tree)
 * - Duration: preset chips + custom number input
 * - Optional note
 * - Optional startedAt override (defaults to now - duration)
 */
export function QuickLogTimeDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [taskId, setTaskId] = useState('')
  const [durationMinutes, setDurationMinutes] = useState<number>(15)
  const [note, setNote] = useState('')

  // Fetch active tasks (flattened) + project names (for label) — only
  // when dialog is open. Project name is NOT inlined in /agile/tree
  // response, so we fetch /projects separately and join client-side.
  const treeQuery = useQuery({
    enabled: open,
    queryKey: ['agile', 'tree', { activeOnly: true }],
    queryFn: () => apiClient.get<AgileTree>('/agile/tree?activeOnly=true'),
  })

  const projectsQuery = useQuery({
    enabled: open,
    queryKey: ['projects', { all: true }],
    queryFn: () => apiClient.get<ProjectsResult>('/projects?limit=200'),
  })

  const projectNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of projectsQuery.data?.results ?? []) m.set(p.id, p.name)
    return m
  }, [projectsQuery.data])

  const flatTasks: FlatTask[] = useMemo(() => {
    const features = treeQuery.data?.features ?? []
    return features.flatMap((feature) => {
      const projectLabel = feature.projectId
        ? (projectNameById.get(feature.projectId) ?? '—')
        : '—'
      const stories = feature.userStories ?? []
      return stories.flatMap((us) => {
        const tasks = us.tasks ?? []
        return tasks
          .filter((t) => t.status === 'in_progress' || t.status === 'todo')
          .map((t) => ({
            id: t.id,
            label: `${projectLabel} · ${feature.title} · ${t.title}`,
            estimatedHours: t.estimatedHours ? Number(t.estimatedHours) : null,
          }))
      })
    })
  }, [treeQuery.data, projectNameById])

  const mutation = useMutation({
    mutationFn: (body: { taskId: string; durationMinutes: number; note?: string }) =>
      apiClient.post('/time/manual-entry', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['time'] })
      // Reset and close
      setTaskId('')
      setNote('')
      setDurationMinutes(15)
      onClose()
    },
  })

  const submit = () => {
    if (!taskId || durationMinutes <= 0) return
    mutation.mutate({ taskId, durationMinutes, note: note.trim() || undefined })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Quick Log Time"
      description="Registra rapidamente lavoro fatto fuori da Claude (call, IDE, email a mano) per chiudere il gap di copertura tracking."
      size="md"
    >
      <div className="p-5 space-y-4">
        {/* Task picker */}
        <div>
          <label htmlFor="quick-log-task" className="block text-xs font-medium text-zinc-400 mb-1.5">
            Task
          </label>
          {treeQuery.isLoading ? (
            <div className="text-sm text-zinc-500">Carico task attivi…</div>
          ) : flatTasks.length === 0 ? (
            <div className="text-sm text-amber-400">
              Nessun task attivo trovato. Creane uno via WebUI Agile o skill /track-work.
            </div>
          ) : (
            <Select
              id="quick-log-task"
              value={taskId}
              onChange={(e) => setTaskId(e.currentTarget.value)}
              className="w-full"
            >
              <option value="">Seleziona task…</option>
              {flatTasks.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                  {t.estimatedHours ? ` (stima ${t.estimatedHours}h)` : ''}
                </option>
              ))}
            </Select>
          )}
        </div>

        {/* Duration */}
        <div>
          <label
            htmlFor="quick-log-duration"
            className="block text-xs font-medium text-zinc-400 mb-1.5"
          >
            Durata (minuti)
          </label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {DURATION_PRESETS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setDurationMinutes(m)}
                className={
                  durationMinutes === m
                    ? 'px-3 py-1 text-xs rounded-md bg-cyan-500/20 text-cyan-300 border border-cyan-400/40'
                    : 'px-3 py-1 text-xs rounded-md bg-zinc-900 text-zinc-400 border border-zinc-800 hover:border-zinc-700'
                }
              >
                {m}m
              </button>
            ))}
          </div>
          <Input
            id="quick-log-duration"
            type="number"
            min={1}
            max={1440}
            step={5}
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(Number(e.currentTarget.value) || 0)}
            className="w-32"
          />
        </div>

        {/* Note */}
        <div>
          <label htmlFor="quick-log-note" className="block text-xs font-medium text-zinc-400 mb-1.5">
            Nota (opzionale)
          </label>
          <Input
            id="quick-log-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            placeholder="es. Call cliente, refactor manuale, scrittura email…"
            className="w-full"
          />
        </div>

        {/* Error */}
        {mutation.isError && (
          <div className="text-sm text-red-400">
            Errore:{' '}
            {mutation.error instanceof Error ? mutation.error.message : 'Salvataggio fallito'}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Annulla
          </Button>
          <Button
            onClick={submit}
            disabled={!taskId || durationMinutes <= 0 || mutation.isPending}
          >
            <Plus className="size-4" />
            {mutation.isPending ? 'Salvataggio…' : `Registra ${durationMinutes}m`}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
